import React, {ReactElement} from "react";
import {PathComponent} from "index";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "../shared/BaseFragment";
import {Button, Card, List, ListItem, Typography} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow, StyledContainer, StyledEmpty, StyledSpan} from "../shared/StyledComponents";
import {ArrowForwardIosOutlined, CheckOutlined} from "@mui/icons-material";
import {
  BORDER_RADIUS,
  DIVIDER,
  PAGE_FRAGMENT_SM_WIDTH,
  PD_LG,
  PD_SM,
  PD_XLG,
  PD_XLG2,
  SZ_XLG,
  SZ_XXXLG
} from "../shared/dimens";
import {lightGray, translucentWhite, white} from "../shared/colors";
import {COLOR_PRIMARY} from "./theme";
import {Camera, CameraPredictionFrame} from "./Camera";
import {BaseApp} from "../shared/BaseApp";
import {App} from "./App";
import {ImageEditorImage, ImageEditorImageType} from "../shared/StyledCamera";
import Checkbox from "@mui/material/Checkbox";
import {ReactCompareSlider, ReactCompareSliderImage} from "react-compare-slider";
import {Point, Rect} from "./types";
import {sleep} from "../shared/sleep";
import {Mysmile, MysmileCustomizeOptions} from "../mysmile-shared/types";
import {Action, ProfilePhoto} from "../shared/types";
import defaultPractice from "./res/images/default_practice.png";
import {FormGenContainer} from "../shared/FormGenContainer";
import {ContactRequest, ContactRequests, ContactRequestSource} from "../libwyze-shared/types";
import {DialogFragment, PageFragment, PageFragmentProps} from "../shared/PageFragment";

type ContactRequestFormFragmentProps = PageFragmentProps & {
  practiceId: string,
  onSubmitDone?: () => void,
}

class ContactRequestFormFragment extends DialogFragment<ContactRequestFormFragmentProps> {

  private readonly request = ContactRequest.createNew(ContactRequestSource.MYSMILE);

  protected styleFlags(): number {
    return PageFragment.STYLE_ACTION_PANEL_TYPE_BUTTON_FLAG;
  }

  protected getActionButtonText(): string {
    return "Submit";
  }

  async onActionButtonClicked(event?): Promise<boolean> {
    await new ContactRequests(this.props.practiceId).addListItem(this.request);
    setTimeout(() => {
      this.props.onSubmitDone?.();
    }, 100);
    return true;
  }

  renderContent(): ReactElement {
    return <StyledBoxColumn>
      <Typography variant={"body2"}>Please fill out the form below and your dentist will get in touch with
        you.</Typography>
      <FormGenContainer content={this.request} saveActionButtonProvider={this}/>
    </StyledBoxColumn>;
  }
}

type FaceResult = {
  before: ImageEditorImage,
  after?: ImageEditorImage,
}

enum ViewMode {
  SELECT_PRACTICE,
  TAKE_PHOTO,
  FILL_FORM,
  IMAGE,
  SUBMIT_INFO,
}

export type MainProps = BaseFragmentProps & {}

type MainState = BaseFragmentState & {
  practiceId?: string,
  viewMode: ViewMode,
  options?: MysmileCustomizeOptions,
  result?: FaceResult,
  takePhotoClicked?: boolean,
  ageChecked?: boolean,
  marketingChecked?: boolean,
}

export class Main extends BaseFragment<MainProps, MainState> {

  constructor(props: MainProps, context: any) {
    super(props, context);
    this.state = {
      viewMode: ViewMode.SELECT_PRACTICE,
    }
  }

  static nestedPaths(): PathComponent[] {
    return [];
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    const params = new URLSearchParams(window.location.search);
    const practiceId = params.get("id");
    this.setState({
      practiceId,
    });
    if (practiceId) {
      await Mysmile.loadInstance(practiceId);
      this.setState({
        options: Mysmile.getInstance()?.customizeOptions,
      });
    }
  }

  private continueNoPractice(): void {
    const params = new URLSearchParams(window.location.search);
    params.delete("id");
    window.location.search = params.toString();
  }

  protected renderContainerContent(): React.ReactElement | null {
    const practiceId = this.state.practiceId;
    const options = this.state.options;
    if (practiceId && (!options?.isLoaded() || !options.enabled)) {
      return <StyledEmpty emptyConfig={{
        iconUrl: "/images/error.svg",
        title: "There was an error loading your mySmile",
        text: "Please check the url and try again.",
        action: new Action("Continue", () => this.continueNoPractice())
      }}/>;
    }
    return <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        background: lightGray,
        // paddingTop: 40,
        // paddingBottom: 24,
        overflow: "hidden",
      }}>
      {!this.state.result
        ? <img src={"/images/hero.svg"}
               style={{position: "absolute", height: "150dvh", top: "4%", right: "-50%", opacity: 0.25}}/>
        : null}
      <div style={{display: "flex", flexDirection: "column", flexGrow: 1, overflowY: 'scroll'}}>
        <StyledBoxRow style={{
          position: "fixed",
          zIndex: 1,
          background: translucentWhite,
          width: "100%",
          alignItems: "center",
          padding: `0px ${PD_XLG}px`,
          height: 96,
          flexShrink: 0,
        }}>
          <img src={"/images/dentwyze_logotype.svg"} style={{height: 40}}/>
          <img src={"/images/dentwyze_mysmile_logotype.svg"} style={{height: 48, marginBottom: -4}}/>
          <StyledSpan/>
        </StyledBoxRow>
        {this.renderViewMode()}
      </div>
    </div>;
  }

  private renderViewMode(): ReactElement {
    switch (this.state.viewMode) {
      default:
      case ViewMode.SELECT_PRACTICE:
        return this.renderSelectPractice();
      case ViewMode.TAKE_PHOTO:
        return this.renderTakePhoto();
      case ViewMode.FILL_FORM:
        return this.renderFillForm();
      case ViewMode.IMAGE:
        return this.renderImage();
      case ViewMode.SUBMIT_INFO:
        return this.renderSubmitInfo();
    }
  }

  private renderSelectPractice(): ReactElement {
    const params = new URLSearchParams(window.location.search);
    const options = this.state.options;
    return <StyledContainer size={"md"} style={{
      marginTop: SZ_XLG,
      justifyContent: "center",
      gap: PD_XLG2,
    }}>
      <StyledBoxColumn style={{
        padding: PD_XLG2,
        background: white,
        borderRadius: BORDER_RADIUS,
        border: DIVIDER,
        // borderTop: "4px solid " + COLOR_PRIMARY,
        gap: PD_XLG,
      }}>
        {Boolean(options)
          ? <StyledBoxColumn style={{alignItems: "center"}}>
            <Card style={{width: SZ_XLG, aspectRatio: 1}}>
              <img src={ProfilePhoto(options.profilePhoto, defaultPractice)}
                   style={{width: "100%", height: "100%", objectFit: "contain"}}/>
            </Card>
            <Typography variant={"h6"} style={{textAlign: "center"}}>
              {options.displayName}
            </Typography>
          </StyledBoxColumn>
          : null}
        <Typography>
          <b>Welcome to mySmile.</b>
        </Typography>
        {Boolean(options)
          ? <>
            <Typography>
              {`Experience mySmile to see the potential of a new smile after your treatment at ${options.displayName}.`}
            </Typography>
            <StyledBoxColumn style={{width: "100%", alignItems: "center", justifyContent: "center"}}>
              <Button
                onClick={() => this.setState({viewMode: ViewMode.TAKE_PHOTO})}
                endIcon={<ArrowForwardIosOutlined/>} style={{fontSize: "100%", padding: `${PD_SM}px ${PD_XLG2}px`}}
                variant={"contained"}>
                Continue
              </Button>
            </StyledBoxColumn>
          </>
          : <>
            <Typography>
              If you're at a practice, open the camera app and scan QR code.
            </Typography>
            {params.get("g") !== "0"
              ? <Typography>
                OR <Button onClick={() => this.setState({viewMode: ViewMode.TAKE_PHOTO})}>Continue as Guest</Button>.
              </Typography>
              : null}
          </>}
      </StyledBoxColumn>
    </StyledContainer>;
  }

  private renderFillForm(): ReactElement {
    return <>
      <StyledContainer size={"md"} style={{
        marginTop: SZ_XLG,
        justifyContent: "center",
        gap: PD_XLG2,
      }}>
        <ContactRequestFormFragment
          practiceId={this.state.practiceId}
          onSubmitDone={() => this.setState({viewMode: ViewMode.IMAGE})}/>
      </StyledContainer>
    </>
  }

  private renderTakePhoto(): ReactElement {
    const main = this;
    return <>
      <StyledContainer size={"md"} style={{
        marginTop: SZ_XXXLG,
        justifyContent: "center",
        gap: PD_XLG2,
        paddingBottom: "8%",
      }}>
        <StyledBoxColumn style={{
          padding: PD_XLG2,
          background: white,
          borderRadius: BORDER_RADIUS,
          border: DIVIDER,
          // borderTop: "4px solid " + COLOR_PRIMARY,
          gap: PD_XLG,
        }}>
          <img src={"/images/mysmile_face.jpg"}
               style={{
                 alignSelf: "center",
                 width: 160,
                 aspectRatio: 1,
                 marginTop: `-${80 + 48}px`,
                 marginBottom: PD_XLG,
                 border: "4px solid " + COLOR_PRIMARY
               }}/>
          <Typography variant={"h6"}>
            Take a smiling selfie and we'll show you how your smile can be transformed.
            <br/><br/>
            For best results, make sure that you:
          </Typography>
          <List>
            <ListItem><StyledBoxRow><CheckOutlined/>Ensure sufficient light</StyledBoxRow></ListItem>
            <ListItem><StyledBoxRow><CheckOutlined/>Remove glasses or hats</StyledBoxRow></ListItem>
            <ListItem><StyledBoxRow><CheckOutlined/>Keep your head straight</StyledBoxRow></ListItem>
            <ListItem><StyledBoxRow><CheckOutlined/>Look at camera and smile brightly</StyledBoxRow></ListItem>
          </List>
          <Button
            onClick={() => {
              this.setState({takePhotoClicked: true});
              if (!this.state.ageChecked || !this.state.marketingChecked) {
                return;
              }
              BaseApp.CONTEXT.showProgressDialog("Please wait...", null, async function () {
                new Camera(await App.LANDMARKER).show((image, frame) => {
                  // Set the partial result first, so that the fullscreen dialog shows up.
                  main.setState({
                    viewMode: main.state.practiceId ? ViewMode.FILL_FORM : ViewMode.IMAGE,
                    result: {before: image.data},
                  });
                  BaseApp.CONTEXT.showProgressDialog("Please wait...", null, async function () {
                    main.setState({result: await main.genResult(image, frame)});
                  });
                });
              });
            }}
            variant={"contained"}>Take a Selfie</Button>
          <StyledBoxColumn>
            <StyledBoxRow style={{alignItems: "flex-start"}}>
              <Checkbox onChange={(e, checked) => this.setState({ageChecked: checked})}/>
              <StyledBoxColumn style={{flexGrow: 1, marginTop: 12}}>
                {this.state.takePhotoClicked && !this.state.ageChecked
                  ? <Typography variant={"caption"} style={{color: "red"}}>
                    You must agree to this to continue.
                  </Typography>
                  : null}
                <Typography variant={"caption"}>
                  I confirm that I am 18 years or older.
                </Typography>
              </StyledBoxColumn>
            </StyledBoxRow>
            <StyledBoxRow style={{alignItems: "flex-start"}}>
              <Checkbox onChange={(e, checked) => this.setState({marketingChecked: checked})}/>
              <StyledBoxColumn style={{flexGrow: 1, marginTop: 12}}>
                {this.state.takePhotoClicked && !this.state.marketingChecked
                  ? <Typography variant={"caption"} style={{color: "red"}}>
                    You must agree to this to continue.
                  </Typography>
                  : null}
                <Typography variant={"caption"}>
                  I agree to receive information about treatments and other products from Dentwyze, its customers,
                  partners
                  and affiliates, by e-mail, WhatsApp, phone or other digital means to which I consent. This may contain
                  information on special offers, appointment scheduling, product information, useful tips, as well as
                  requests for feedback about my treatment and consultation experience.
                </Typography>
              </StyledBoxColumn>
            </StyledBoxRow>
          </StyledBoxColumn>
        </StyledBoxColumn>
      </StyledContainer>
      <Typography variant={"caption"} style={{zIndex: 1, padding: PD_LG, color: "white", background: COLOR_PRIMARY}}>
        Disclaimer: mySmile is not intended to convey or constitute medical, dental or treatment advice. mySmile
        images are not provided in the course of and does not create or constitute any doctor-patient or
        dentist-patient relationship. mySmile provides only an example of one of the potential results of
        orthodontic
        treatment, and this result may not achievable by you. The final outcome of any treatment will depend on a
        number of factors, such as your overall dental condition, the period of treatment, compliance with
        directions
        from your doctor/dentist, your doctor/dentist’s approach and best practices and guidelines relating to the
        use
        of any orthodontic treatment. To generate examples, mySmile may demonstrate correction of other common
        dental
        issues that will not be corrected by your treatment, such as discolouration, cracks, and missing or uneven
        teeth, etc. Image quality will differ depending on your device. For more information, consult your
        doctor/dentist.
      </Typography>
    </>;
  }

  private loadTemplate(): Promise<HTMLImageElement> {
    const template = new Image();
    const promise = new Promise<HTMLImageElement>(resolve => {
      template.onload = () => {
        resolve(template);
      }
    });
    template.src = "/images/template.png";
    return promise;
  }

  private async genResult(image: ImageEditorImage, frame: CameraPredictionFrame): Promise<FaceResult> {
    await sleep(2000);
    const canvas = document.createElement("canvas");
    const template = await this.loadTemplate();
    const img = new Image();
    let p: Point;
    const after: ImageEditorImage = {
      data: await new Promise(resolve => {
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          // Because we're mirrored, flip.
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
          ctx.strokeStyle = "green";
          ctx.lineWidth = 2;
          let i: number;
          i = 0;
          const rect = new Rect();
          p = {x: frame.lipsOuter[i].x * canvas.width, y: frame.lipsOuter[i].y * canvas.height}
          rect.add(p.x, p.y);
          while (++i < frame.lipsOuter.length) {
            p = {x: frame.lipsOuter[i].x * canvas.width, y: frame.lipsOuter[i].y * canvas.height}
            rect.add(p.x, p.y);
          }
          i = 0;
          ctx.beginPath();
          p = {x: frame.lipsInner[i].x * canvas.width, y: frame.lipsInner[i].y * canvas.height}
          ctx.moveTo(p.x, p.y);
          while (++i < frame.lipsInner.length) {
            p = {x: frame.lipsInner[i].x * canvas.width, y: frame.lipsInner[i].y * canvas.height}
            ctx.lineTo(p.x, p.y);
          }
          ctx.closePath();
          ctx.clip();
          const yof = 0.13; // y-offset factor
          const swf = 0.86;  // scaled width factor
          ctx.drawImage(template, 0, template.height * yof, template.width, template.height, rect.topLeft.x + rect.width() * (1 - swf) / 2, rect.topLeft.y, rect.width() * swf, rect.height());
          // ctx.stroke();
          resolve(canvas.toDataURL("image/jpeg"));
        }
        img.src = image.data;
      }),
      type: ImageEditorImageType.BASE64URL
    }
    return {
      before: image,
      after: after,
    };
  }

  private renderImage(): ReactElement {
    const result = this.state.result;
    if (!result) {
      return null;
    }
    return <div
      style={{display: "flex", flexDirection: "column", height: "100dvh", overflow: "hidden"}}>
      <div style={{
        width: "100%",
        flexGrow: 1,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: "black",
      }}>
        {this.state.result.before && this.state.result.after
          ? <ReactCompareSlider
            style={{width: "100%", height: "100%",}}
            itemOne={<ReactCompareSliderImage src={result.before.data} style={{objectFit: "contain"}}/>}
            itemTwo={<ReactCompareSliderImage src={result.after.data} style={{objectFit: "contain"}}/>}
          />
          : null
        }
      </div>
      <StyledBoxColumn style={{
        position: "absolute",
        left: 0,
        bottom: 0,
        width: "100%",
        padding: PD_LG,
        background: translucentWhite,
        alignItems: "center"
      }}>
        <Button
          style={{width: "100%", maxWidth: PAGE_FRAGMENT_SM_WIDTH}}
          onClick={() => {
            this.setState({viewMode: ViewMode.SELECT_PRACTICE, result: null});
          }}
          variant={"contained"}>
          Done
        </Button>
      </StyledBoxColumn>
    </div>;
  }

  private renderSubmitInfo(): ReactElement {
    return null;
  }
}
